import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { hapticMedium } from '../../utils/haptics';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** "sheet" = bottom on mobile, centered on desktop. "alert" = always centered. */
  variant?: 'sheet' | 'alert';
  title?: string;
  showHandle?: boolean;
  zIndex?: number;
  children: React.ReactNode;
}

/** Check if viewport is >= 640px (Tailwind sm breakpoint) */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

/** Dismiss threshold — if dragged beyond this fraction of sheet height, dismiss */
const DISMISS_THRESHOLD = 0.25;
/** Velocity threshold (px/ms) — fast flick dismisses regardless of distance */
const VELOCITY_THRESHOLD = 0.4;

/**
 * iOS-native bottom sheet / alert component with swipe-to-dismiss.
 *
 * - **sheet**: Slides up from bottom on mobile (swipeable), centered on desktop.
 * - **alert**: Always centered (like iOS UIAlertController, no swipe).
 *
 * Uses portal for proper z-stacking and locks body scroll when open.
 */
export function BottomSheet({
  isOpen,
  onClose,
  variant = 'sheet',
  title,
  showHandle = true,
  zIndex = 100,
  children,
}: BottomSheetProps) {
  const [isClosing, setIsClosing] = useState(false);
  const isDesktop = useIsDesktop();
  const scrollYRef = useRef(0);

  // Swipe state
  const sheetRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startTime = useRef(0);
  const currentY = useRef(0);
  const touchStartedOnHandle = useRef(false);

  // Can swipe only on mobile sheet variant
  const canSwipe = variant === 'sheet' && !isDesktop;

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, scrollYRef.current);
      };
    }
  }, [isOpen]);

  // Reset drag when opening + haptic
  useEffect(() => {
    if (isOpen) {
      setDragY(0);
      isDragging.current = false;
      touchStartedOnHandle.current = false;
      hapticMedium();
    }
  }, [isOpen]);

  // Keyboard avoidance — adjust sheet when virtual keyboard opens on mobile
  useEffect(() => {
    if (!isOpen || !window.visualViewport || isDesktop) return;
    const vv = window.visualViewport;
    const handleResize = () => {
      const keyboardHeight = window.innerHeight - vv.height;
      if (sheetRef.current) {
        if (keyboardHeight > 50) {
          sheetRef.current.style.maxHeight = `${vv.height * 0.9}px`;
          sheetRef.current.style.transform = `translateY(-${keyboardHeight}px)`;
        } else {
          sheetRef.current.style.maxHeight = '';
          sheetRef.current.style.transform = '';
        }
      }
    };
    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, [isOpen, isDesktop]);

  // Auto-scroll focused inputs into view inside sheet
  useEffect(() => {
    if (!isOpen) return;
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        if (sheetRef.current?.contains(target)) {
          setTimeout(() => {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 300);
        }
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      setIsClosing(false);
      setDragY(0);
      onClose();
    }
  }, [isClosing, onClose]);

  // Backdrop tap closes
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) handleClose();
    },
    [handleClose]
  );

  // --- Touch handlers for swipe-to-dismiss ---
  // We need to distinguish between scrolling inside content and swiping the sheet down.
  // Strategy: Only start a sheet drag if:
  //   1. Touch started on the handle/title area, OR
  //   2. Touch started on content but the content is scrolled to top and user drags down

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!canSwipe) return;

    const touch = e.touches[0];
    startY.current = touch.clientY;
    currentY.current = 0;
    startTime.current = Date.now();

    // Check if touch started on handle area (top 60px of sheet)
    const sheetEl = sheetRef.current;
    if (sheetEl) {
      const sheetRect = sheetEl.getBoundingClientRect();
      const touchRelativeY = touch.clientY - sheetRect.top;
      touchStartedOnHandle.current = touchRelativeY < 60;
    }

    // Don't set isDragging yet — wait for touchMove to determine direction
    isDragging.current = false;
  }, [canSwipe]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!canSwipe) return;

    const delta = e.touches[0].clientY - startY.current;

    // If we haven't started dragging yet, determine if we should
    if (!isDragging.current) {
      // Only start dragging downward
      if (delta < 5) return;

      // If touch didn't start on handle, check if inner content is scrolled
      if (!touchStartedOnHandle.current) {
        // Find the scrollable container inside the sheet
        const scrollableEl = sheetRef.current?.querySelector('[data-sheet-scroll]') as HTMLElement | null;
        if (scrollableEl && scrollableEl.scrollTop > 0) {
          // Content is scrolled — let native scroll handle it
          return;
        }
      }

      isDragging.current = true;
    }

    // Only allow downward drag (positive delta)
    const clamped = Math.max(0, delta);
    currentY.current = clamped;
    setDragY(clamped);

    // Prevent native scroll while dragging the sheet
    e.preventDefault();
  }, [canSwipe]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current || !canSwipe) {
      isDragging.current = false;
      return;
    }
    isDragging.current = false;

    const sheetHeight = sheetRef.current?.offsetHeight || 300;
    const elapsed = Date.now() - startTime.current;
    const velocity = currentY.current / Math.max(elapsed, 1); // px/ms

    // Dismiss if dragged past threshold OR fast flick
    if (currentY.current > sheetHeight * DISMISS_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      // Animate out from current position
      setDragY(sheetHeight);
      // After transition completes, close
      setTimeout(() => {
        setDragY(0);
        onClose();
      }, 300);
    } else {
      // Snap back with spring
      setDragY(0);
    }
  }, [canSwipe, onClose]);

  if (!isOpen && !isClosing) return null;

  const isSheet = variant === 'sheet';
  const useCenteredAnim = !isSheet || isDesktop;

  // Choose animation based on variant + breakpoint
  const animClass = useCenteredAnim
    ? (isClosing ? 'animate-sheet-fade-out' : 'animate-sheet-fade-in')
    : (isClosing ? 'animate-sheet-down' : 'animate-sheet-up');

  // Backdrop opacity fades as user drags
  const backdropOpacity = canSwipe && dragY > 0 && !isClosing
    ? Math.max(0, 1 - dragY / (sheetRef.current?.offsetHeight || 300))
    : undefined;

  const content = (
    <div
      className={`fixed inset-0 flex justify-center transition-colors duration-200 ${
        isClosing ? 'bg-transparent' : 'bg-black/40 backdrop-blur-[2px]'
      } ${isSheet && !isDesktop ? 'items-end' : 'items-center'}`}
      style={{
        zIndex,
        ...(backdropOpacity !== undefined ? { backgroundColor: `rgba(0,0,0,${0.4 * backdropOpacity})` } : {}),
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title || undefined}
        className={`
          bg-[var(--bg-surface)] shadow-ios-sheet w-full overflow-hidden overflow-y-auto
          ${isSheet && !isDesktop
            ? 'rounded-t-ios-sheet'
            : 'rounded-ios-sheet max-w-sm mx-4'
          }
          ${dragY > 0 ? '' : animClass}
        `}
        style={{
          ...(isSheet && !isDesktop ? { paddingBottom: 'max(0px, env(safe-area-inset-bottom))' } : {}),
          ...(canSwipe && dragY > 0 ? {
            transform: `translateY(${dragY}px)`,
            transition: isDragging.current ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          } : {}),
        }}
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={handleAnimationEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Handle bar (sheet only, mobile) — visual swipe affordance */}
        {isSheet && !isDesktop && showHandle && (
          <div ref={handleRef} className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing">
            <div className="w-10 h-1 bg-[var(--text-tertiary)] rounded-full opacity-30" />
          </div>
        )}

        {/* Optional title */}
        {title && (
          <div className={`px-4 ${isSheet && !isDesktop && showHandle ? 'pt-1 pb-3' : 'pt-4 pb-3'} text-center`}>
            <h3 className="text-sm font-medium text-[var(--text-secondary)]">{title}</h3>
          </div>
        )}

        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default BottomSheet;
